import { RootState } from '@/store/store.config';
import axios, { AxiosRequestConfig } from 'axios';
import { useState } from 'react';
import { useSelector } from 'react-redux';

interface DownloadOptions<TRequestData, TResponse> {
    onSuccess?: (response: TResponse) => void;
    onError?: (error: unknown) => void;
    url: string;
    requestData: TRequestData;
    config?: AxiosRequestConfig;
}

export function useFileDownload() {
    const token = useSelector((state: RootState) => state.userStore.token);
    const apiConfig = useSelector((state: RootState) => state.configStore.apiHost);
    const [progress, setProgress] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<unknown>(null);

    async function download<TRequestData, TResponse>(options: DownloadOptions<TRequestData, TResponse>) {
        const { url, onSuccess, onError, requestData, config = {} } = options;

        setLoading(true);
        setError(null);
        setProgress(0);

        try {
            const response = await axios.request<TResponse>({
                url: `${apiConfig?.host || ''}${url}`,
                method: 'POST',
                data: requestData,
                responseType: 'arraybuffer',
                headers: {
                    'Content-Type': 'application/json',
                    token: token,
                },
                ...config,
                onDownloadProgress: (progressEvent) => {
                    const total = progressEvent.total || 0;
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / total);
                    setProgress(percentCompleted);
                },
            });

            setLoading(false);
            setProgress(100);
            onSuccess?.(response.data);
        } catch (err) {
            setLoading(false);
            setError(err);
            setProgress(0);
            onError?.(err);
        }
    }

    return {
        download,
        progress,
        loading,
        error,
    };
}